import { useState } from "react";
import { motion } from "framer-motion";
import { Quote, TrendingUp, ArrowUpRight, Target, Lightbulb, Workflow } from "lucide-react";
import { AreaChart, Area, ResponsiveContainer, XAxis, Tooltip } from "recharts";
import PageHero from "@/components/PageHero";
import CTABand from "@/components/CTABand";

const cases = [
  {
    id: "homestack",
    industry: "Real Estate",
    client: "HomeStack Realty",
    headline: "From 12 leads/mo to 480+ qualified leads/mo",
    color: "#FF5E00", color2: "#FF007F",
    quote: "Novarise rebuilt our entire growth stack. The pipeline is unrecognizable.",
    author: "Founder, HomeStack",
    sections: {
      overview: "A premium real estate firm with strong word-of-mouth but no predictable pipeline.",
      problem: "Long sales cycles, leaky intake, no attribution, manual follow-ups across 4 channels.",
      strategy: "Rebuild brand, plug performance + SEO, deploy AI lead-routing and CRM automation.",
      execution: "Brand refresh → conversion-tuned site → multi-channel campaigns → AI agents in CRM.",
    },
    metrics: [
      { label: "Leads / month", before: 12, after: 480, suffix: "" },
      { label: "Conversion rate", before: 1.1, after: 6.4, suffix: "%", decimals: 1 },
      { label: "Revenue lift", before: 0, after: 14.2, prefix: "₹", suffix: "Cr", decimals: 1 },
      { label: "Ops time saved", before: 0, after: 62, suffix: "%" },
    ],
    data: [
      { m: "Jan", v: 12 }, { m: "Feb", v: 28 }, { m: "Mar", v: 62 },
      { m: "Apr", v: 140 }, { m: "May", v: 240 }, { m: "Jun", v: 360 }, { m: "Jul", v: 480 },
    ],
  },
  {
    id: "lumen-edu",
    industry: "Education",
    client: "Lumen Academy",
    headline: "Built an AI demand engine generating 9,000 leads / quarter",
    color: "#00F0FF", color2: "#8A2BE2",
    quote: "We unlocked a category-level lead engine — and it just keeps compounding.",
    author: "CMO, Lumen Academy",
    sections: {
      overview: "An ambitious ed-tech brand with strong product but rising CAC and inconsistent enrolments.",
      problem: "Costly paid channels, low show-up rate, no nurture and zero attribution clarity.",
      strategy: "Programmatic content + AI nurture journeys + conversion bots, with full attribution.",
      execution: "Topical SEO + LP factory + lifecycle copilots + dashboards for ops & marketing.",
    },
    metrics: [
      { label: "MQLs / quarter", before: 800, after: 9000, suffix: "" },
      { label: "Cost / lead", before: 720, after: 86, prefix: "₹", suffix: "" },
      { label: "Enrolments lift", before: 0, after: 3.4, suffix: "x", decimals: 1 },
      { label: "Show-up rate", before: 28, after: 64, suffix: "%" },
    ],
    data: [
      { m: "Q1", v: 800 }, { m: "Q2", v: 2200 }, { m: "Q3", v: 5400 }, { m: "Q4", v: 9000 },
    ],
  },
  {
    id: "sigma-health",
    industry: "Healthcare",
    client: "Sigma Health",
    headline: "Automated CRM + AI agents replaced 4 internal roles",
    color: "#FF007F", color2: "#007AFF",
    quote: "We saved 70% of ops time and our patient experience went up, not down.",
    author: "Founder, Sigma Health",
    sections: {
      overview: "A multi-clinic healthcare brand drowning in manual ops across appointments, intake and follow-ups.",
      problem: "Fragmented data, no-shows, manual reminders, and a back-office team stretched thin.",
      strategy: "Unified CRM, AI scheduling agents, automated reminders, predictive no-show flagging.",
      execution: "Migration → AI agents → automation studio → live dashboards for clinic ops.",
    },
    metrics: [
      { label: "Ops time saved", before: 0, after: 71, suffix: "%" },
      { label: "Patient bookings", before: 1.0, after: 2.6, suffix: "x", decimals: 1 },
      { label: "Retention", before: 62, after: 91, suffix: "%" },
      { label: "No-show rate", before: 22, after: 6, suffix: "%" },
    ],
    data: [
      { m: "W1", v: 12 }, { m: "W4", v: 28 }, { m: "W8", v: 46 }, { m: "W12", v: 58 }, { m: "W16", v: 71 },
    ],
  },
];

export default function CaseStudiesPage() {
  const [active, setActive] = useState(cases[0].id);
  const cur = cases.find((c) => c.id === active);

  return (
    <div data-testid="case-studies-root">
      <PageHero
        eyebrow="Case Studies"
        title={<>Real systems. Real <span className="text-gradient-neon">compounding</span> results.</>}
        subtitle="Three deep-dives into how brand, growth and automation combine to move revenue."
      />

      <section className="container-x">
        <div className="flex flex-wrap gap-2 mb-10">
          {cases.map((c) => (
            <button
              key={c.id}
              data-testid={`case-tab-${c.id}`}
              onClick={() => setActive(c.id)}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                active === c.id ? "bg-white text-bg-0" : "bg-white/5 text-white/70 hover:bg-white/10"
              }`}
            >
              {c.industry} · {c.client}
            </button>
          ))}
        </div>

        <motion.div key={cur.id} initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} data-testid="case-study-active">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
            <div className="lg:col-span-5 glass rounded-3xl p-8 relative overflow-hidden">
              <div className="absolute -top-32 -left-32 w-80 h-80 rounded-full blur-3xl opacity-40"
                   style={{ background: `radial-gradient(circle, ${cur.color}, transparent)` }} />
              <p className="overline mb-4">{cur.industry} · {cur.client}</p>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight leading-tight">{cur.headline}</h2>

              <div className="mt-6 space-y-5">
                <Block icon={Lightbulb} label="Overview" body={cur.sections.overview} />
                <Block icon={Target} label="Problem" body={cur.sections.problem} />
                <Block icon={Workflow} label="Strategy" body={cur.sections.strategy} />
                <Block icon={ArrowUpRight} label="Execution" body={cur.sections.execution} />
              </div>
            </div>

            <div className="lg:col-span-7 space-y-5">
              <div className="glass rounded-3xl p-8 relative overflow-hidden">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="overline">Growth Curve</p>
                    <div className="mt-1 flex items-center gap-2">
                      <TrendingUp size={18} className="text-emerald-400" />
                      <span className="font-mono text-xl font-bold">Trajectory: up & right</span>
                    </div>
                  </div>
                </div>

                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={cur.data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id={`gc-${cur.id}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={cur.color} stopOpacity={0.85} />
                          <stop offset="100%" stopColor={cur.color2} stopOpacity={0.05} />
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="m" stroke="rgba(255,255,255,0.3)" tick={{ fontSize: 11 }} />
                      <Tooltip
                        cursor={{ stroke: "rgba(255,255,255,0.2)" }}
                        contentStyle={{
                          background: "rgba(10,10,18,0.92)",
                          border: "1px solid rgba(255,255,255,0.1)",
                          borderRadius: 12,
                          fontFamily: "JetBrains Mono",
                          fontSize: 12,
                        }}
                      />
                      <Area type="monotone" dataKey="v" stroke={cur.color} strokeWidth={2.5} fill={`url(#gc-${cur.id})`} animationDuration={1200} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
                  {cur.metrics.map((m) => (
                    <div key={m.label} className="rounded-xl bg-white/[0.03] border border-white/10 p-4">
                      <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-white/45">{m.label}</div>
                      <div className="mt-2 flex items-baseline gap-2">
                        <span className="text-white/40 line-through text-xs">{m.prefix || ""}{m.before}{m.suffix || ""}</span>
                        <span className="font-mono text-base font-extrabold" style={{ color: cur.color }}>
                          {m.prefix || ""}{m.after}{m.suffix || ""}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="glass rounded-3xl p-7">
                <Quote size={22} className="text-white/40" />
                <p className="mt-3 text-lg leading-relaxed">&ldquo;{cur.quote}&rdquo;</p>
                <p className="mt-3 font-mono text-xs tracking-[0.18em] uppercase text-white/45">{cur.author}</p>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      <CTABand
        headline={<>Want results like these? <span className="text-gradient-warm">Let&apos;s talk.</span></>}
        sub="A 45-minute working session — walk away with a custom growth blueprint."
        primary={{ label: "Book Strategy Call", to: "/contact" }}
        secondary={{ label: "See Our Services", to: "/services" }}
      />
    </div>
  );
}

function Block({ icon: Icon, label, body }) {
  return (
    <div className="flex gap-3">
      <span className="w-8 h-8 rounded-md bg-white/[0.06] border border-white/10 flex items-center justify-center mt-0.5 shrink-0">
        <Icon size={14} />
      </span>
      <div>
        <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/45">{label}</div>
        <p className="mt-1 text-sm text-white/75 leading-relaxed">{body}</p>
      </div>
    </div>
  );
}
